/**
 * Spike floor hazard system.
 *
 * Starting from a configurable level threshold, places patches of
 * spike tiles on corridor paths. Stepping on spikes applies a timed
 * "wounded" status effect (10 seconds) that passively drains health,
 * slows movement, and leaves a blood trail while moving.
 *
 * Spike patches are placed deterministically using the maze's seeded RNG
 * so they regenerate identically from the same seed (no need to persist
 * spike positions in the save file).
 *
 * Spikes NEVER overlap with water, lava, or poison tiles — they are
 * placed after all three systems and exclude all their cell positions.
 *
 * Spike tiles feature an animated up/down motion via a scale-Y tween
 * to simulate spikes extending and retracting from the floor.
 */

import {
  TILE_SIZE,
  SPIKE_MIN_LEVEL,
  SPIKE_PATCH_MIN_LENGTH,
  SPIKE_PATCH_MAX_LENGTH,
  SPIKE_MAX_PATCHES_BASE,
  SPIKE_PATCHES_PER_LEVEL,
  SPIKE_COVERAGE_CAP,
} from '../config/game-config.js';
import { TEX_SPIKE, CellType } from '../utils/constants.js';
import MazeGenerator from './MazeGenerator.js';

export default class SpikeSystem {
  /**
   * @param {Phaser.Scene} scene - The GameScene instance.
   * @param {number[][]} grid - The maze grid (expanded).
   * @param {number} level - Current game level.
   * @param {{ col: number, row: number }} startPos - Player start position (excluded from spikes).
   * @param {{ col: number, row: number }} exitPos - Exit position (excluded from spikes).
   * @param {number} [seed] - Optional seed for deterministic placement.
   * @param {Set<string>} [waterCells] - Set of "col,row" keys for water tiles.
   * @param {Set<string>} [lavaCells] - Set of "col,row" keys for lava tiles.
   * @param {Set<string>} [poisonCells] - Set of "col,row" keys for poison tiles.
   */
  constructor(scene, grid, level, startPos, exitPos, seed, waterCells, lavaCells, poisonCells) {
    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {number[][]} */
    this.grid = grid;

    /** @type {number} */
    this.level = level;

    /** @type {{ col: number, row: number }} */
    this.startPos = startPos;

    /** @type {{ col: number, row: number }} */
    this.exitPos = exitPos;

    /** @type {Set<string>} Water cell positions to avoid overlapping. */
    this.waterCells = waterCells || new Set();

    /** @type {Set<string>} Lava cell positions to avoid overlapping. */
    this.lavaCells = lavaCells || new Set();

    /** @type {Set<string>} Poison cell positions to avoid overlapping. */
    this.poisonCells = poisonCells || new Set();

    /** @type {Set<string>} Set of "col,row" strings for O(1) lookup. */
    this.spikeCells = new Set();

    /** @type {Phaser.GameObjects.Sprite[]} Spike tile sprites for cleanup. */
    this.sprites = [];

    /** @type {Phaser.Tweens.Tween[]} Animation tweens for cleanup. */
    this.tweens = [];

    // Seeded RNG (simple LCG) — use a unique XOR offset to avoid
    // identical random sequences even when sharing the same base seed
    this._rngState = (seed ?? (Date.now() ^ 0xdeadbeef)) ^ 0xdeadbeef;

    // Only place spikes from the minimum level onward
    if (level >= SPIKE_MIN_LEVEL) {
      this._placeSpikePatches();
      this._renderSpikeSprites();
    }
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Check whether a grid cell is a spike tile.
   *
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  isSpike(col, row) {
    return this.spikeCells.has(`${col},${row}`);
  }

  /**
   * Destroy all spike sprites and tweens. Call during level cleanup.
   */
  destroy() {
    for (const tween of this.tweens) {
      if (tween && tween.isPlaying) {
        tween.stop();
      }
    }
    this.tweens = [];

    for (const sprite of this.sprites) {
      if (sprite && sprite.active) {
        sprite.destroy();
      }
    }
    this.sprites = [];

    this.spikeCells.clear();
    this.scene = null;
  }

  // ---------------------------------------------------------------
  // Private — placement
  // ---------------------------------------------------------------

  /**
   * Simple LCG pseudo-random number generator.
   * @private
   * @returns {number} Value in [0, 1).
   */
  _nextRandom() {
    this._rngState = (this._rngState * 1664525 + 1013904223) & 0x7fffffff;
    return this._rngState / 0x80000000;
  }

  /**
   * Return a random integer in [min, max] (inclusive).
   * @private
   */
  _randInt(min, max) {
    return min + Math.floor(this._nextRandom() * (max - min + 1));
  }

  /**
   * Find corridors (straight runs of PATH cells) in the maze.
   * A corridor is a sequence of 2+ adjacent PATH cells in a straight
   * line (horizontal or vertical).
   *
   * @private
   * @returns {{ cells: { col: number, row: number }[], direction: 'h' | 'v' }[]}
   */
  _findCorridors() {
    const corridors = [];
    const gridH = this.grid.length;
    const gridW = this.grid[0].length;

    // Horizontal corridors
    for (let row = 0; row < gridH; row++) {
      let run = [];
      for (let col = 0; col < gridW; col++) {
        if (this.grid[row][col] === CellType.PATH) {
          run.push({ col, row });
        } else {
          if (run.length >= 2) {
            corridors.push({ cells: run, direction: 'h' });
          }
          run = [];
        }
      }
      if (run.length >= 2) {
        corridors.push({ cells: run, direction: 'h' });
      }
    }

    // Vertical corridors
    for (let col = 0; col < gridW; col++) {
      let run = [];
      for (let row = 0; row < gridH; row++) {
        if (this.grid[row][col] === CellType.PATH) {
          run.push({ col, row });
        } else {
          if (run.length >= 2) {
            corridors.push({ cells: run, direction: 'v' });
          }
          run = [];
        }
      }
      if (run.length >= 2) {
        corridors.push({ cells: run, direction: 'v' });
      }
    }

    return corridors;
  }

  /**
   * Place spike patches on randomly selected corridor segments.
   * Avoids the player start cell, exit cell, all water, lava, and poison cells.
   * @private
   */
  _placeSpikePatches() {
    const corridors = this._findCorridors();
    if (corridors.length === 0) return;

    // How many patches to attempt
    const levelsAboveMin = this.level - SPIKE_MIN_LEVEL;
    const targetPatches = SPIKE_MAX_PATCHES_BASE + levelsAboveMin * SPIKE_PATCHES_PER_LEVEL;

    // Coverage cap: don't cover more than SPIKE_COVERAGE_CAP of all path cells
    const pathCells = MazeGenerator.getPathCells(this.grid);
    const maxSpikeCells = Math.floor(pathCells.length * SPIKE_COVERAGE_CAP);

    // Excluded positions (start and exit, plus their immediate neighbours)
    const excluded = new Set();
    excluded.add(`${this.startPos.col},${this.startPos.row}`);
    excluded.add(`${this.exitPos.col},${this.exitPos.row}`);
    // Also exclude 1-tile radius around start/exit for comfort
    for (const pos of [this.startPos, this.exitPos]) {
      for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]]) {
        excluded.add(`${pos.col + dx},${pos.row + dy}`);
      }
    }

    // Shuffle corridors using Fisher-Yates with our seeded RNG
    const shuffled = [...corridors];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(this._nextRandom() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    let patchesPlaced = 0;

    for (const corridor of shuffled) {
      if (patchesPlaced >= targetPatches) break;
      if (this.spikeCells.size >= maxSpikeCells) break;

      // Only use corridors long enough for a spike patch
      if (corridor.cells.length < SPIKE_PATCH_MIN_LENGTH) continue;

      // Pick a random sub-segment of the corridor
      const patchLen = this._randInt(
        SPIKE_PATCH_MIN_LENGTH,
        Math.min(SPIKE_PATCH_MAX_LENGTH, corridor.cells.length),
      );
      const maxStart = corridor.cells.length - patchLen;
      const startIdx = this._randInt(0, maxStart);

      // Collect candidate cells for this patch
      const candidates = [];
      let valid = true;
      for (let i = startIdx; i < startIdx + patchLen; i++) {
        const cell = corridor.cells[i];
        const key = `${cell.col},${cell.row}`;
        // Exclude start/exit neighbours, existing spikes, water, lava, AND poison cells
        if (
          excluded.has(key)
          || this.spikeCells.has(key)
          || this.waterCells.has(key)
          || this.lavaCells.has(key)
          || this.poisonCells.has(key)
        ) {
          valid = false;
          break;
        }
        candidates.push(cell);
      }

      if (!valid || candidates.length === 0) continue;

      // Add the patch
      for (const cell of candidates) {
        this.spikeCells.add(`${cell.col},${cell.row}`);
      }
      patchesPlaced++;
    }
  }

  // ---------------------------------------------------------------
  // Private — rendering
  // ---------------------------------------------------------------

  /**
   * Create spike tile sprites in the scene for each spike cell.
   * Sprites are placed above the floor but below the fog layer.
   * Uses a batched tween that oscillates scale-Y per spike to simulate
   * spikes extending up and retracting, with per-sprite phase offsets
   * for visual variety.
   * @private
   */
  _renderSpikeSprites() {
    if (!this.scene) return;

    /** @type {{ sprite: Phaser.GameObjects.Sprite, phase: number }[]} */
    const spriteData = [];

    for (const key of this.spikeCells) {
      const [colStr, rowStr] = key.split(',');
      const col = parseInt(colStr, 10);
      const row = parseInt(rowStr, 10);

      const x = col * TILE_SIZE + TILE_SIZE / 2;
      const y = row * TILE_SIZE + TILE_SIZE / 2;

      const sprite = this.scene.add.sprite(x, y, TEX_SPIKE);
      sprite.setOrigin(0.5);
      sprite.setDepth(2); // Above walls (0), below player/food/coins (5) and fog (100)
      sprite.setAlpha(0.85);

      // Random phase offset for visual variety
      const phase = this._nextRandom() * Math.PI * 2;

      this.sprites.push(sprite);
      spriteData.push({ sprite, phase });
    }

    // Single batched animation tween: oscillates scale-Y to simulate
    // spikes going up and down, plus a subtle alpha pulse
    if (spriteData.length > 0) {
      const animState = { t: 0 };

      const animTween = this.scene.tweens.add({
        targets: animState,
        t: Math.PI * 2,
        duration: 1800,   // Full cycle duration for spike up/down
        repeat: -1,
        ease: 'Linear',
        onUpdate: () => {
          for (const { sprite, phase } of spriteData) {
            if (!sprite.active) continue;
            // Oscillate scaleY between 0.7 (retracted) and 1.0 (extended)
            const wave = Math.sin(animState.t + phase);
            sprite.scaleY = 0.85 + 0.15 * wave;
            // Subtle alpha pulse to emphasize the metallic glint
            sprite.setAlpha(0.80 + 0.10 * wave);
          }
        },
      });

      this.tweens.push(animTween);
    }
  }
}
